export function partitionMembers<T extends { id: string; agencySubGeometryId: string | null }>(
  members: T[],
  subGeometryId: string,
): { attached: T[]; candidates: T[] } {
  const attached: T[] = [];
  const candidates: T[] = [];
  for (const m of members) {
    if (m.agencySubGeometryId === subGeometryId) attached.push(m);
    else candidates.push(m);
  }
  return { attached, candidates };
}

/**
 * Draft state for a member in the manage dialog, derived from whether the server currently has
 * it attached to this sub region and whether the local draft wants it attached:
 * - `committed`: on the server AND in the draft — no pending change.
 * - `pending-add`: in the draft but not yet on the server — staged assignment.
 * - `pending-remove`: on the server but dropped from the draft — staged unassignment.
 */
export type MemberDraftState = 'committed' | 'pending-add' | 'pending-remove';

export function deriveMemberDraftState(serverAttached: boolean, inDraft: boolean): MemberDraftState {
  if (serverAttached && inDraft) return 'committed';
  return inDraft ? 'pending-add' : 'pending-remove';
}

/**
 * Diff the locally-staged draft set against the server's currently-attached set to find the
 * assignment changes the Save button must commit. `toAttach` is what the user added (in the draft,
 * not yet on the server); `toDetach` is what the user removed (on the server, dropped from the draft).
 */
export function computeMemberDiff(
  serverAttachedIds: Iterable<string>,
  draftIds: Iterable<string>,
): { toAttach: string[]; toDetach: string[] } {
  const server = new Set(serverAttachedIds);
  const draft = new Set(draftIds);
  const toAttach: string[] = [];
  const toDetach: string[] = [];
  for (const id of draft) if (!server.has(id)) toAttach.push(id);
  for (const id of server) if (!draft.has(id)) toDetach.push(id);
  return { toAttach, toDetach };
}
